import {
  BANKVERIFICATIONSTATUS,
  ExternalPayoutStatus,
  IBankVerification,
  IPayment,
} from "@shared/lib/types/briefCampains";
import styles from "./paymentExternalStatus.module.scss";

type PaymentExternalStatusProps = {
  record: IPayment;
};

function getStatusLabel(status?: ExternalPayoutStatus | string): string {
  if (status === ExternalPayoutStatus.Success) return "Paid";
  return status || "—";
}

function getStatusTone(
  status?: ExternalPayoutStatus | string
): "pending" | "queued" | "processing" | "success" | "failed" | "cancelled" {
  switch (status) {
    case ExternalPayoutStatus.Queued:
      return "queued";
    case ExternalPayoutStatus.Processing:
      return "processing";
    case ExternalPayoutStatus.Success:
      return "success";
    case ExternalPayoutStatus.Failed:
      return "failed";
    case ExternalPayoutStatus.Cancelled:
      return "cancelled";
    default:
      return "pending";
  }
}

function maskAccountNumber(accountNumber?: string): string {
  if (!accountNumber) return "";
  return accountNumber.length <= 4
    ? accountNumber
    : `••••${accountNumber.slice(-4)}`;
}

function getBankDetails(
  bankVerification?: Partial<IBankVerification>,
  status?: BANKVERIFICATIONSTATUS
) {
  const isVerified =
    status === BANKVERIFICATIONSTATUS.Verified ||
    bankVerification?.status === BANKVERIFICATIONSTATUS.Verified;
  if (!isVerified || !bankVerification) return null;

  const response = bankVerification.verificationResponse;
  const accountNumber = bankVerification.bankAccountNumber || "";
  if (!accountNumber && !bankVerification.bankAccountName) return null;

  return {
    bankName: response?.bank_name || response?.ifsc_details?.bank,
    accountNumber,
  };
}

export function PaymentExternalStatus({ record }: PaymentExternalStatusProps) {
  const status = record.externalPayoutStatus;
  const tone = getStatusTone(status);
  const utr = record.externalUtrNo;
  const reason = record.externalPayoutRejectionReason;
  const bank = getBankDetails(
    record.bankVerification,
    record.bankVerificationStatus
  );
  const showBank =
    status !== ExternalPayoutStatus.Pending &&
    status !== ExternalPayoutStatus.Cancelled &&
    Boolean(bank);

  return (
    <div className={styles.cell}>
      <span className={`${styles.pill} ${styles[tone]}`}>
        {getStatusLabel(status)}
      </span>
      {utr ? <div className={styles.meta}>UTR {utr}</div> : null}
      {status === ExternalPayoutStatus.Failed && reason ? (
        <div className={styles.reason}>{reason}</div>
      ) : null}
      {status === ExternalPayoutStatus.Cancelled && reason ? (
        <div className={styles.reason}>{reason}</div>
      ) : null}
      {showBank && bank ? (
        <div className={styles.bank}>
          {bank.bankName ? `${bank.bankName} · ` : ""}
          {maskAccountNumber(bank.accountNumber)}
        </div>
      ) : null}
    </div>
  );
}
